// ABOUTME: Where a guest's stable session id lives. One place, because three call sites need it:
// ABOUTME: the WS connect URL, the wishlist paint-cache key, and the force_reconnect teardown.

// SPEC-39 OQ3 — reverses SPEC-31 G3. The anonymous id used to live in `sessionStorage`, which dies
// with the tab. That made the server-side selection TTL unreachable for anonymous guests: the bag
// outlived the only thing that could claim it, so a 30-day snapshot just became orphaned bytes.
// localStorage is what the identified path (`versai_sid_<customer>`) already used, and is what
// commercetools recommends for an anonymous cart pointer.
//
// Consequence, deliberate: localStorage is shared across tabs, so two anonymous tabs in the same
// browser now share one session id and therefore one bag. That is the point — it is also exactly
// what identified guests have always done. It is NOT cross-device; nothing here reaches beyond
// this browser profile.
//
// Storage access is wrapped because Safari private mode throws on access rather than returning
// null, and an unguarded read here would white-screen the app before the boutique ever opens.

const ANON_KEY = 'versai_sid';
const identifiedKey = (customer) => `versai_sid_${customer}`;

function safeGet(store, key) {
  try { return store.getItem(key); } catch { return null; }
}
function safeSet(store, key, value) {
  try { store.setItem(key, value); } catch { /* storage unavailable — run without persistence */ }
}
function safeRemove(store, key) {
  try { store.removeItem(key); } catch { /* ditto */ }
}

/** The anonymous id if one is already stored, else null. Never mints — callers that render before
 *  the socket opens (the wishlist key) must not create an identity for a guest who never enters. */
export function readAnonSid() {
  return safeGet(localStorage, ANON_KEY) || safeGet(sessionStorage, ANON_KEY) || null;
}

/** The stored id for this guest, or null. `customer` comes from the `?customer=` URL param. */
export function readStoredSid(customer) {
  if (customer) return safeGet(localStorage, identifiedKey(customer));
  return readAnonSid();
}

/** The id to connect with: reuse what is stored, otherwise mint and persist. The sessionStorage
 *  read inside `readAnonSid` is the migration — a guest who was mid-visit when this shipped keeps
 *  their id, and therefore their bag and their wishlist cache key, instead of being handed a new
 *  one. The old copy is then dropped so it cannot linger and confuse a later debug session. */
export function resolveSessionId(customer, mint = () => crypto.randomUUID()) {
  const sid = readStoredSid(customer) || mint();
  if (customer) {
    safeSet(localStorage, identifiedKey(customer), sid);
  } else {
    safeSet(localStorage, ANON_KEY, sid);
    safeRemove(sessionStorage, ANON_KEY);
  }
  return sid;
}

/** Drop every stored id for this guest. Used by the force_reconnect path, where the backend has
 *  told us its snapshot is gone and the id is worth nothing. Clears both stores so a half-migrated
 *  guest cannot be resurrected from the sessionStorage copy on the next connect. */
export function clearStoredSessionId(customer) {
  safeRemove(localStorage, ANON_KEY);
  safeRemove(sessionStorage, ANON_KEY);
  if (customer) safeRemove(localStorage, identifiedKey(customer));
}
